import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/shared/lib/cn';

const buttonVariants = cva(
  // Base: 44px minimum touch target on the default size, logical spacing so
  // icon gaps mirror correctly in RTL, and a disabled state that reads as
  // disabled rather than merely faded.
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-[background-color,color,box-shadow,opacity] outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*=size-])]:size-5',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-card hover:bg-primary/90',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        outline: 'border border-input bg-card text-foreground hover:bg-muted',
        ghost: 'text-foreground hover:bg-muted',
        subtle: 'bg-accent text-accent-foreground hover:bg-accent/70',
        destructive:
          'bg-destructive text-destructive-foreground shadow-card hover:bg-destructive/90',
        success: 'bg-success text-success-foreground shadow-card hover:bg-success/90',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-5 has-[>svg]:px-4',
        sm: 'h-9 gap-1.5 rounded-md px-3 text-xs [&_svg:not([class*=size-])]:size-4',
        lg: 'h-13 rounded-xl px-6 text-base',
        icon: 'size-11',
        'icon-sm': 'size-9 rounded-md [&_svg:not([class*=size-])]:size-4',
      },
      block: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ComponentPropsWithoutRef<'button'>, VariantProps<typeof buttonVariants> {
  /** Render as the single child element (Radix `Slot`), e.g. a router `Link`. */
  asChild?: boolean;
  /**
   * Shows a spinner and blocks interaction. Prefer this over disabling the
   * button manually so the reason for the disabled state stays visible.
   */
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, block, asChild = false, loading = false, children, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';

  // `asChild` hands rendering to the child, so a spinner would have nowhere to
  // go without breaking Slot's single-child contract.
  const content =
    loading && !asChild ? (
      <>
        <Loader2 className="size-4 animate-spin" aria-hidden />
        {children}
      </>
    ) : (
      children
    );

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, block }), className)}
      disabled={loading || props.disabled}
      aria-busy={loading || undefined}
      {...props}
    >
      {content}
    </Comp>
  );
});

export { buttonVariants };
